// Explore Campaigns page for influencers
// Shows all available campaigns in a scrollable grid

import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import { collection, query, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { db } from '../../../firebaseConfig.js';
import InfluencerCampaignCard from './InfluencerCampaignCard';
import Navbar from '../../components/layout/Navbar';
import Sidebar from '../../components/layout/Sidebar';
import { normalizeApplicationStatus } from '../lib/applicationStatus.js';

function isValidCampaign(data) {
  if (!data) return false;
  // Only accept "approved" or "active" status (campaigns must be approved by admin)
  const validStatuses = ['approved', 'active'];
  const status = data.status || 'pending_approval';
  const statusValid = validStatuses.includes(status);
  // Deadline can be a string (new format) or Timestamp (legacy)
  const deadlineValid =
    !!data.deadline &&
    (typeof data.deadline === 'string' || (data.deadline && typeof data.deadline.toDate === 'function'));
  return statusValid && deadlineValid && Boolean(data.collaborationType);
}

const ExploreCampaigns = ({ onNavigate }) => {
  const { user, profile } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [applications, setApplications] = useState([]);
  const [brandLogos, setBrandLogos] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Redirect non-influencers
  useEffect(() => {
    if (!loading && profile && profile.userType !== 'influencer') {
      onNavigate('dashboard');
    }
  }, [profile, loading, onNavigate]);

  // Fetch all available campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user || profile?.userType !== 'influencer') {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch campaigns - only show approved/active campaigns (must be approved by admin)
        const constraints = [where('status', 'in', ['approved', 'active']), limit(100)];

        const q = query(collection(db, 'campaigns'), ...constraints);
        const snap = await getDocs(q);

        let docs = snap.docs
          .map((d) => ({ id: d.id, data: d.data() }))
          .filter(({ data }) => isValidCampaign(data));

        // Sort client-side by createdAt (newest first)
        docs.sort((a, b) => {
          const aDate = a.data.createdAt?.toDate?.() || new Date(0);
          const bDate = b.data.createdAt?.toDate?.() || new Date(0);
          return bDate.getTime() - aDate.getTime();
        });

        setCampaigns(docs);

        // Fetch brand logos
        const uniqueBrandIds = [
          ...new Set(docs.map(({ data }) => data.brandId || data.brandUid).filter(Boolean)),
        ];
        const logosMap = {};

        await Promise.all(
          uniqueBrandIds.map(async (brandId) => {
            try {
              const brandRef = doc(db, 'users', brandId);
              const brandSnap = await getDoc(brandRef);
              if (brandSnap.exists()) {
                const brandData = brandSnap.data();
                logosMap[brandId] = brandData.logoUrl || null;
              }
            } catch (err) {
              console.warn(`⚠️ Could not fetch brand logo for ${brandId}:`, err);
              logosMap[brandId] = null;
            }
          })
        );

        setBrandLogos(logosMap);
      } catch (e) {
        console.error('Error fetching campaigns:', e);
        setError(e?.message ?? 'Failed to load campaigns.');
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [user, profile]);

  // Fetch applications for current influencer
  useEffect(() => {
    const fetchApplications = async () => {
      if (!user || profile?.userType !== 'influencer') return;

      try {
        const q = query(
          collection(db, 'applications'),
          where('influencerId', '==', user.uid)
        );
        const snap = await getDocs(q);
        const apps = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setApplications(apps);
      } catch (err) {
        console.error('Error fetching applications:', err);
      }
    };

    fetchApplications();
  }, [user, profile]);

  // Derive applied campaign IDs (exclude withdrawn)
  const appliedCampaignIds = useMemo(() => {
    return new Set(
      applications
        .filter((app) => {
          const status = normalizeApplicationStatus(app.status);
          // Only treat as "applied" if status is pending_review, accepted, or rejected
          // Exclude withdrawn if it exists
          return status !== 'withdrawn';
        })
        .map((app) => app.campaignId)
    );
  }, [applications]);

  const handleApply = async () => {
    // Refetch applications to update the UI
    if (!user) return;
    try {
      const q = query(collection(db, 'applications'), where('influencerId', '==', user.uid));
      const snap = await getDocs(q);
      const apps = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));
      setApplications(apps);
    } catch (err) {
      console.error('Error refetching applications:', err);
    }
  };

  if (loading && campaigns.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="space-y-6">
              <div>
                <h1 className="text-2xl font-semibold">Explore Campaigns</h1>
                <p className="text-sm text-gray-500">Browse all active campaigns and apply to collaborate.</p>
              </div>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="h-64 rounded-2xl bg-gray-100 animate-pulse" />
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar onNavigate={onNavigate} />
        <div className="flex">
          <Sidebar onNavigate={onNavigate} />
          <main className="flex-1 p-6">
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
              <span>{error}</span>
              <button onClick={() => window.location.reload()} className="ml-3 underline">
                Retry
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar onNavigate={onNavigate} />
      <div className="flex">
        <Sidebar onNavigate={onNavigate} />
        <main className="flex-1 p-6">
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold">Explore Campaigns</h1>
              <p className="text-sm text-gray-500">
                Scroll through all available campaigns and apply to the ones that fit your content.
              </p>
            </div>

            {campaigns.length === 0 ? (
              <div className="text-sm text-gray-500">
                There are no active campaigns available right now. Check back soon!
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {campaigns.map((campaign) => {
                  const hasApplied = appliedCampaignIds.has(campaign.id);
                  const brandId = campaign.data.brandId || campaign.data.brandUid;
                  // Find the application status for this campaign
                  const application = applications.find((app) => app.campaignId === campaign.id);
                  const applicationStatus = application?.status || null;
                  return (
                    <InfluencerCampaignCard
                      key={campaign.id}
                      campaign={campaign}
                      brandLogoUrl={brandId ? brandLogos[brandId] || null : null}
                      hasApplied={hasApplied}
                      applicationStatus={applicationStatus}
                      onApply={handleApply}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ExploreCampaigns;

